import { SENIOR_SECTION, CardData } from "../../data";

/**
 * The senior-era mission run: 20 granular tickets (4 per engagement)
 * with a deterministic schedule over scroll progress p ∈ [0, 1].
 * Round-robin across engagements keeps several tickets in flight at
 * once; each engagement's final ticket lands in display order, so the
 * board ships vista → imogen → ap+ → urbnsurf → virgin, matching the
 * compiled rows below the board.
 */

/* Deterministic pseudo-jitter so the schedule reads organic, not metronomic. */
const jit = (n: number) => ((n * 7919) % 97) / 97;

export type Engagement = {
  card: CardData;
  /** Ticket prefix — VST-01 etc. */
  tag: string;
  /** Lowercase agent name used in the feed. */
  short: string;
  /** One-line project descriptor shown on ticket cards. */
  desc: string;
};

const META: Record<string, { tag: string; short: string; desc: string }> = {
  vista: { tag: "VST", short: "vista", desc: "Vista — agentic orchestrator" },
  imed: { tag: "IMO", short: "imogen", desc: "I-MED — IMOGEN chatbot" },
  applus: { tag: "APP", short: "ap+", desc: "AP+ — payments frontend" },
  urbnsurf: {
    tag: "URB",
    short: "urbnsurf",
    desc: "UrbnSurf — booking platform",
  },
  virginactive: {
    tag: "VIR",
    short: "virgin",
    desc: "Virgin Active — gym platform",
  },
};

type TicketDef = { title: string; ac: [string, string] };

const TICKET_DEFS: Record<string, TicketDef[]> = {
  vista: [
    {
      title: "Extract business logic into workflows",
      ac: ["Rules mapped to skills", "Workflows replayable end-to-end"],
    },
    {
      title: "Wire GitHub · Azure · Copilot",
      ac: ["PRs raised autonomously", "CI green on every handoff"],
    },
    {
      title: "Multi-model routing via OpenRouter",
      ac: ["Right model per task", "Fallbacks under rate limits"],
    },
    {
      title: "WinForms → Angular/C# pipeline",
      ac: ["1980–2026 code, one pipeline", "Plans actioned by orchestrator"],
    },
  ],
  imed: [
    {
      title: "Solution architecture with I-MED",
      ac: ["Solution design signed off", "Risks logged with owners"],
    },
    {
      title: "AI messaging middleware",
      ac: ["Replies grounded + on-brand", "P95 latency within budget"],
    },
    {
      title: "Genesys human escalation",
      ac: ["Seamless human handover", "Context carried into Genesys"],
    },
    {
      title: "Pre-delivery → go-live",
      ac: ["UAT passed with I-MED", "Go-live, zero rollbacks"],
    },
  ],
  applus: [
    {
      title: "Frontend delivery (NDA)",
      ac: ["Meets a11y + brand specs", "Details sealed under NDA"],
    },
    {
      title: "Gen-e2 seminar — plan with AI",
      ac: ["Teams planning with AI", "Plans reviewed for quality"],
    },
    {
      title: "Gen-e2 seminar — code with quality",
      ac: ["AI code passes review bar", "Quality gates adopted"],
    },
    {
      title: "Consulting playbooks",
      ac: ["Playbooks repeatable", "Adopted across squads"],
    },
  ],
  urbnsurf: [
    {
      title: "Guest + member booking flows",
      ac: ["Both paths book E2E", "Multiple flows covered"],
    },
    {
      title: "Auth, dashboard, memberships",
      ac: ["Secure auth + sessions", "Memberships self-serve"],
    },
    {
      title: "Multipacks + gift cards",
      ac: ["Multipacks redeemable", "Gift cards purchasable"],
    },
    {
      title: "CMS for custom pages",
      ac: ["Editors publish, no deploys", "Site data fully editable"],
    },
  ],
  virginactive: [
    {
      title: "New site across SG · TH · AU",
      ac: ["Three regions, one codebase", "Localised per market"],
    },
    {
      title: "MyLocker product frontend",
      ac: ["Lockers bookable in-app", "Solid on gym-floor mobile"],
    },
    {
      title: "Feature consulting + rollout",
      ac: ["Features spec'd with client", "Staged rollout, no regressions"],
    },
    {
      title: "Handover + hardening",
      ac: ["Docs + handover complete", "Perf + security hardened"],
    },
  ],
};

export const ENGAGEMENTS: Engagement[] = SENIOR_SECTION.cards.map((card) => ({
  card,
  ...META[card.project.id],
}));

export type Ticket = {
  key: string;
  eng: Engagement;
  title: string;
  /** Acceptance criteria, ticked off while the ticket is in flight. */
  ac: [string, string];
  /** Progress thresholds: appears in backlog / starts / in review / done. */
  spawn: number;
  start: number;
  review: number;
  done: number;
  /** When each acceptance criterion checks off (during in-progress). */
  acAt: [number, number];
  /** Gate stamps while in review. */
  crAt: number;
  qaAt: number;
};

const ROUNDS = 4;

export const TICKETS: Ticket[] = (() => {
  const out: Ticket[] = [];
  for (let round = 0; round < ROUNDS; round++) {
    ENGAGEMENTS.forEach((eng, e) => {
      const g = round * ENGAGEMENTS.length + e;
      const spawn = 0.02 + g * 0.021 + jit(g) * 0.008;
      const start = spawn + 0.055 + jit(g + 31) * 0.03;
      const review = start + 0.09 + jit(g + 57) * 0.05;
      const done = review + 0.055 + jit(g + 83) * 0.03;
      const def = TICKET_DEFS[eng.card.project.id][round];
      out.push({
        key: `${eng.tag}-${String(round + 1).padStart(2, "0")}`,
        eng,
        title: def.title,
        ac: def.ac,
        spawn,
        start,
        review,
        done,
        acAt: [
          start + (review - start) * (0.4 + jit(g + 7) * 0.1),
          start + (review - start) * (0.78 + jit(g + 11) * 0.12),
        ],
        crAt: review + (done - review) * 0.35,
        qaAt: review + (done - review) * 0.7,
      });
    });
  }
  return out;
})();

/** Board column for a ticket at progress p; -1 = not spawned yet. */
export const stateOf = (t: Ticket, p: number): -1 | 0 | 1 | 2 | 3 => {
  if (p < t.spawn) return -1;
  if (p < t.start) return 0;
  if (p < t.review) return 1;
  if (p < t.done) return 2;
  return 3;
};

/** Progress at which each engagement's last ticket completes. */
export const SHIP_AT: Record<string, number> = (() => {
  const out: Record<string, number> = {};
  ENGAGEMENTS.forEach((eng) => {
    out[eng.card.project.id] = Math.max(
      ...TICKETS.filter((t) => t.eng === eng).map((t) => t.done),
    );
  });
  return out;
})();

export const RUN_DONE = Math.max(...Object.values(SHIP_AT)) + 0.04;

/* ------------------------------------------------------------- the feed */

export type FeedEvent = {
  t: number;
  from: string;
  to: string;
  subject: string;
  accent?: boolean;
};

export const EVENTS: FeedEvent[] = (() => {
  const ev: FeedEvent[] = [
    {
      t: 0.004,
      from: "orch",
      to: "you",
      subject: "run senior_era · 20 tickets cut",
      accent: true,
    },
  ];
  TICKETS.forEach((t) => {
    ev.push({
      t: t.spawn,
      from: "pm",
      to: "board",
      subject: `${t.key} queued`,
    });
    ev.push({
      t: t.start,
      from: "orch",
      to: t.eng.short,
      subject: `assigned · ${t.title.toLowerCase()}`,
    });
    ev.push({
      t: t.review,
      from: t.eng.short,
      to: "qa",
      subject: `${t.key} review requested`,
    });
    ev.push({
      t: t.done,
      from: "qa",
      to: "orch",
      subject: `${t.key} passed ✓`,
    });
  });
  ENGAGEMENTS.forEach((eng) => {
    ev.push({
      t: SHIP_AT[eng.card.project.id] + 0.006,
      from: "orch",
      to: "you",
      subject: `${eng.short} shipped ✓`,
      accent: true,
    });
  });
  ev.push({
    t: RUN_DONE + 0.03,
    from: "orch",
    to: "you",
    subject: "run complete · 5 engagements shipped",
    accent: true,
  });
  return ev.sort((a, b) => a.t - b.t);
})();

/* --------------------------------------------------- re-render triggers */

/**
 * Every progress value at which the board visually changes. The board
 * re-renders only when scroll crosses one of these, not per frame.
 */
export const THRESHOLDS: number[] = (() => {
  const ts = new Set<number>();
  TICKETS.forEach((t) => {
    ts.add(t.spawn);
    ts.add(t.start);
    ts.add(t.review);
    ts.add(t.done);
    ts.add(t.acAt[0]);
    ts.add(t.acAt[1]);
    ts.add(t.crAt);
    ts.add(t.qaAt);
  });
  EVENTS.forEach((e) => ts.add(e.t));
  Object.values(SHIP_AT).forEach((v) => ts.add(v));
  ts.add(RUN_DONE + 0.03);
  return Array.from(ts).sort((a, b) => a - b);
})();

/** Number of thresholds at or below p (binary search). */
export const stepFor = (p: number): number => {
  let lo = 0;
  let hi = THRESHOLDS.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (THRESHOLDS[mid] <= p) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

export const progressAt = (step: number): number =>
  step <= 0 ? 0 : THRESHOLDS[Math.min(step, THRESHOLDS.length) - 1];

/** Fake run clock — the full run "takes" 8 minutes. */
export const fmtClock = (t: number): string => {
  const s = Math.round(Math.max(0, Math.min(1, t)) * 480);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};
